import React, { useEffect } from 'react';
import { PageContainer } from '@amzn/stencil-react-components/page';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QualitySummary } from './summaries/QualitySummary';
import { ProductivitySummary } from './summaries/ProductivitySummary';
import { FeedbackSummary } from './summaries/FeedbackSummary';
import { PositiveFeedbackSummary } from './summaries/PositiveFeedbackSummary';
import { RequestHelp } from './RequestHelp';
import { AuditSummary } from './summaries/AuditSummary';
import { Header } from './Header';
import { Home } from './Home';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { RequireAuth } from '../components/RequireAuth';
import { useTranslation } from 'react-i18next';
import { useGlobalContext, useUserDataContext } from '../hooks';
import { metricSender } from '../util/metric_sender';

const SESSION_METRICS_REFRESH_INTERVAL = 5_000; // 5 seconds

export const App = ({}) => {
  const { t } = useTranslation();
  const { userDetails } = useGlobalContext();
  const { areMetricsSendable, spprSummaries, sqprSummaries, sessionStartTime } = useUserDataContext();

  useEffect(() => {
    /**
     * We emit a session metric every 5 seconds after the user's data is loaded since it is difficult to
     * send session metrics only right before the user closes the browser tab.
     */
    if (areMetricsSendable) {
      const handleSessionMetrics = () => {
        metricSender.generateSessionMetrics(
          userDetails,
          spprSummaries,
          sqprSummaries,
          sessionStartTime,
        );
      };
      handleSessionMetrics();
      const interval = setInterval(handleSessionMetrics, SESSION_METRICS_REFRESH_INTERVAL);
      return () => {
        clearInterval(interval);
      };
    }
    return;
  }, [areMetricsSendable]);

  return (
    <>
      <BrowserRouter>
        <ErrorBoundary>
          <Header />
          <PageContainer paddingTop="S500">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/safety" element={
                <RequireAuth>
                  <FeedbackSummary
                    title="contributeSafety"
                    feedbackCategories={['Safety']}
                    subtitle={`${t('safetyFeedbacksSubtitle')} ${t('yesterdaysDataTooltip')}`}
                    metricName="Safety"
                  />
                </RequireAuth>
              } />
              <Route path="/time" element={
                <RequireAuth>
                  <FeedbackSummary
                    title="manageTime"
                    feedbackCategories={['Time']}
                    subtitle={`${t('timeFeedbacksSubtitle')} ${t('yesterdaysDataTooltip')}`}
                    metricName="Time"
                  />
                </RequireAuth>
              } />
              <Route path="/quality" element={
                <RequireAuth>
                  <QualitySummary />
                </RequireAuth>
              } />
              <Route path="/productivity" element={
                <RequireAuth>
                  <ProductivitySummary />
                </RequireAuth>
              } />
              <Route path="/standards" element={
                <RequireAuth>
                  <FeedbackSummary
                    title="followStandards"
                    feedbackCategories={['Standards']}
                    subtitle={`${t('policyFeedbacksSubtitle')} ${t('yesterdaysDataTooltip')}`}
                    metricName="Standards"
                  />
                </RequireAuth>
              } />
              <Route path="/positive-feedbacks" element={
                <RequireAuth>
                  <PositiveFeedbackSummary />
                </RequireAuth>
              } />
              <Route path="/request-support" element={
                <RequireAuth>
                  <RequestHelp />
                </RequireAuth>
              } />
              <Route path="/audits" element={
                <RequireAuth>
                  <AuditSummary />
                </RequireAuth>
              } />
            </Routes>
          </PageContainer>
        </ErrorBoundary>
      </BrowserRouter>
    </>
  );
}

export default App;
